import React, { useEffect, useState } from 'react'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Stats, { Statistic } from '../../stats'
import Stars from '../../stars'

import Description from './description'
import AddBook from './add-book'
import AffiliateBlock from './affiliate'

import { useAuth } from '../../../../data/hooks/auth'
import { useChangeStatus } from '../../../../data/hooks/change-status'
import { ensureBookClub, getBookByIsbn13 } from '../../../../data/book/book'

import { Book, BookWithRating, StatusEnum } from '../../../../data/book/data-types'
import Loader from '../../loader'


type BookInfoProps = {
  book: Book | BookWithRating| { title: string, isbn13: string }
  needsUpdate?: boolean
  onStatusUpdated?: (status: StatusEnum, newClubId?: number) => void
}

// https://www.amazon.co.uk/s?k=isbn%3A9781473215283&tag=byzans-20

const BookInfo: React.FunctionComponent<BookInfoProps> = ({ book: rawBook, needsUpdate, onStatusUpdated }) => {
    const { t } = useTranslation('book')
    const book = useRefreshableBook(rawBook, needsUpdate)
    const [state, changeStatus] = useChangeStatus(book)

    const stats = getStats(book, t)

    const bookRating = (book as BookWithRating)?.rating
    const hasRating = (bookRating?.average || 0) !== 0

    if (state.book == null) {
        return <Container>
            <Loader />
        </Container>
    }

    return (
        <Container>
            <BasicInfo>
                <Title>
                    {state.book.title}
                </Title>
                <Authors>
                    {t('by')} {prepareAuthors(state.book.authors)}
                </Authors>
                <ImagePart hasRating={hasRating}>
                    <Image src={state.book.image || ''} />
                    <ISBN>
                        {state.book.isbn13}
                    </ISBN>
                </ImagePart>
                <RatingContainer hasRating={hasRating}>
                    <Stars total={5} value={bookRating?.average || 0}/>
                    <Rating> {(bookRating?.average || 0).toFixed(2)} </Rating>
                </RatingContainer>
                <Stats stats={stats} justify={'flex-start'} desktopShort />
            </BasicInfo>
            <BuyBookDesktop>
                <AffiliateBlock book={book!} />
            </BuyBookDesktop>
            <Bottom>
                <AddBook book={state.book} changeStatus={async (statusId, token, force) => {
                    const newClubId = await changeStatus(statusId, token, force)
                    onStatusUpdated && onStatusUpdated(statusId, newClubId)
                    return newClubId
                }} />
                <BuyBookTablet>
                    <AffiliateBlock book={book!} />
                </BuyBookTablet>
                <Description book={state.book} />
            </Bottom>
        </Container>
    )
}

export const useRefreshableBook = (book: Book | { title: string, isbn13: string }, initialNeedsUpdate: boolean = false) => {
    const { token } = useAuth()
	const [needsUpdate, setNeedsUpdate] = useState(initialNeedsUpdate)
	const [realBook, setBook] = useState<Book | null>(book as Book)

	useEffect(() => {
		(async () => {
			if (!needsUpdate) {
				return
			}

            // Check if we got a book or just a stub
            const trueBook = book as Book

            if (trueBook && !trueBook.id) {
                try {
                    await ensureBookClub(trueBook, token?.access)
                } catch(e) {
                    console.log(e)
                    return
                }
            }
	
            try {
                const newBook = await getBookByIsbn13(book.isbn13, token?.access)
                setBook(newBook)
            } catch (e) {
                console.log(e)
            }
		})()
	}, [token?.access, book.isbn13])

	useEffect(() => {
		setNeedsUpdate(true)
	}, [book.isbn13])

    return realBook
}

const getStats = (book: Book | null, t: any): Statistic[] => {
    if (book == null) {
        return []
    }

    return [
        {
            label: t('stats.language'),
            type: 'string',
            value: book.ISBN_language ?? null,
        },
        {
            label: t('stats.published'),
            type: 'string',
            value: book.date_published ?? null,
        },
        {
            label: t('stats.pages'),
            type: 'string',
            value: book.pages ?? null,
        }
    ]
}

const prepareAuthors = (authors: Array<string | { name: string }>): string => {
    if (!authors.length) {
        return ""
    }

    const first = authors[0]
    if (typeof first == "string") {
        return authors.join(",")
    }

    const objAuthors = authors as Array<{ name: string }>

    return objAuthors.map(a => a.name).join(",")
}


const Container = styled.div`
    width: 100%;
    height: 100%;

    display: grid;

    justify-items: center;
    align-items: center;

    grid-row-gap: 20px;

    z-index: 10;

    ${up('tablet')} {
        grid-row-gap: 56px;
    }

    ${up('desktop')} {
        grid-template-columns: 1fr auto;
    }
`

const BasicInfo = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    justify-items: center;

    width: 100%;

    color: ${p => p.theme.colors.bg};

    ${up('desktop')} {
        grid-template-columns: auto 1fr;

        justify-items: flex-start;
        grid-column-gap: 60px;
    }
`

const Title = styled.h1`
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;

    margin: 0;
`

const RatingContainer = styled.div<{ hasRating?: boolean }>`
    display: ${p => p.hasRating ? 'flex' : 'none' };
    align-items: center;
    justify-content: center;

    height: 24px;

    margin: 24px 0px;
`

const Rating = styled.div`
    font-weight: bold;
    font-size: 12px;

    margin-left: 10px;
`

const Authors = styled.div`
    font-size: 14px;
    line-height: 24px;

    text-align: center;

    margin: 8px 0 24px 0;
`

const Image = styled.img`
    height: 260px;
`

const ImagePart = styled.div<{ hasRating: boolean }>`
    position: relative;

    display: flex;
    flex-direction: column;
    align-items: center;

    box-shadow: 0px 4px 32px rgba(0, 0, 0, 0.2);
    border-radius: 8px;

    ${up('desktop')} {
        grid-column: 1;
        grid-row: 1 / ${p => p.hasRating ? '5' : '4' };
    }

    overflow: hidden;
`

const ISBN = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    background: rgba(0, 0, 0, 0.3);

    font-weight: bold;
    font-size: 12px;
    line-height: 28px;

    text-align: center;
`

const BuyBookDesktop = styled.div`
    display: none;
    align-self: flex-start;

    ${up('desktop')} {
        display: grid;
    }
`

const BuyBookTablet = styled.div`
    display: grid;
    align-self: flex-start;

    ${up('desktop')} {
        display: none;
    }
`


const Bottom = styled.div`  
    width: 100%;

    display: grid;

    justify-items: center;
    align-items: center;

    grid-row-gap: 20px;

    ${up('tablet')} {
        grid-row-gap: 56px;
    }

    ${up('desktop')} {
        grid-column: 1 / span 2;
    }
`

export default BookInfo